// @ts-strict-ignore
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { BaseLayerElementCreator } from '@3d-scene/layer/creator/base-layer-element-creator';
import { DrillingHoleSizeData } from '@3d-scene/layer/creator/drilling-hole-size-data';
import { DimensionElement } from '@3d-scene/layer/elements/dimension-element';
import { ILayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { DimensionDescriptor } from '@3d-scene/layer/util/dimension-descriptor';
import { DimensionLevelMetricsUtil } from '@3d-scene/layer/util/dimension-level-metrics.util';
import { DimensionMetrics } from '@3d-scene/layer/util/dimension-metrics';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { LayerName } from '@3d-scene/manager/layer-manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { RGBColorValueVO, SimpleTextValueVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { MeshBasicMaterial, Object3D, Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { SceneManager } from '../../manager/scene.manager';
import { ContainerService } from '../../model3d/container.service';
import { ArticleContainerUtils } from '../../model3d/util/article-container-utils';

export abstract class BaseDimensionElementCreator extends BaseLayerElementCreator {
  protected readonly MILLIMETER_UNIT_LOCALE_KEY: string = 'global.unit.label.mm';
  protected readonly OFFSET: number = 150;
  protected readonly DIMENSION_HEIGHT: number = 100;
  protected readonly DIMENSION_SPACING: number = 10;
  protected readonly DIMENSION_EPSILON: number = 0.85;
  private readonly dimensionsArrowLength: number = 3;
  private readonly dimensionsArrowRadius: number = 20;
  private readonly lineAboveArrowLength: number = 20;

  protected blackMaterial: MeshBasicMaterial = new MeshBasicMaterial({ color: 0x000000 });
  protected blackColor: RGBColorValueVO = new RGBColorValueVO();
  protected redColor: RGBColorValueVO = new RGBColorValueVO(255, 0, 0);

  constructor(
    projectModel: ProjectModel,
    protected localeResourceManager: LocaleResourceManager,
    protected sceneManager: SceneManager,
    protected containerService: ContainerService,
    protected articleContainerUtils: ArticleContainerUtils,
    layerName: LayerName,
  ) {
    super(projectModel, layerName);
  }

  protected longestLabelLength: number = Number.MIN_VALUE;

  protected processDimensionDescriptors(
    dimensionDescriptors: DimensionDescriptor[],
    dimensionElements: ILayerElement[],
    createBackground: boolean = false
  ): number {
    let maxSizeLevel: number = 0;
    const dimensionsLevels: Dictionary<number, DimensionMetrics> = new Dictionary<number, DimensionMetrics>();

    for (const dimensionDescriptor of dimensionDescriptors) {
      const dimensionElement: ILayerElement = this.createLayerElement(dimensionDescriptor, dimensionsLevels, createBackground);

      dimensionElements.push(dimensionElement);
      maxSizeLevel = Math.max(maxSizeLevel, dimensionDescriptor.sizeLevel);
      DimensionLevelMetricsUtil.updateDimensionsLevels(
        dimensionsLevels,
        dimensionDescriptor.sizeLevel,
        dimensionDescriptor.minX,
        dimensionDescriptor.maxX
      );
    }

    return maxSizeLevel;
  }

  protected createLayerElement(
    dimensionDescriptor: DimensionDescriptor,
    dimensionsLevels: Dictionary<number, DimensionMetrics>,
    createBackground: boolean = false
  ): ILayerElement {
    dimensionDescriptor.sizeLevel = !dimensionDescriptor.isOuterLabel
      ? 0
      : DimensionLevelMetricsUtil.findAvailableDimensionLevelByMetrics(dimensionsLevels, dimensionDescriptor.minX, dimensionDescriptor.maxX);

    return new DimensionElement(
      this.sceneManager,
      [
        dimensionDescriptor.startPoint,
        dimensionDescriptor.startPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        dimensionDescriptor.endPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        dimensionDescriptor.endPoint,
      ],
      dimensionDescriptor.text,
      this.blackMaterial,
      dimensionDescriptor.sizeLevel,
      DimensionElement.LEVEL_OFFSET,
      dimensionDescriptor.fontSize,
      dimensionDescriptor.fontScale,
      dimensionDescriptor.arrowMultiplier,
      createBackground
    );
  }

  protected createDimensionDescriptor(
    startPoint: Vector3,
    endPoint: Vector3,
    bold: boolean,
    textValue: TextValueVO = null,
    fontSize: number = NaN,
    fontScale: number = NaN,
    arrowMultiplier: number = NaN
  ): DimensionDescriptor {
    const dimensionDescriptor = new DimensionDescriptor();
    dimensionDescriptor.startPoint = startPoint.clone();
    dimensionDescriptor.endPoint = endPoint.clone();
    dimensionDescriptor.text = textValue
      ? textValue
      : this.createTextValue(this.formatDimension(startPoint.distanceTo(endPoint) / 10), bold ? this.redColor : null);
    dimensionDescriptor.fontSize = fontSize;
    dimensionDescriptor.fontScale = fontScale;
    dimensionDescriptor.arrowMultiplier = isNaN(arrowMultiplier) ? DimensionElement.ARROW_MULTIPLIER : arrowMultiplier;

    const textWidth: number = DimensionUtils.getTextWidth(dimensionDescriptor.text as SimpleTextValueVO, fontSize, fontScale);
    dimensionDescriptor.isOuterLabel = DimensionUtils.isDimensionNeedsOuterLabel(startPoint, endPoint, textWidth);

    this.setMinMaxValues(dimensionDescriptor, startPoint, endPoint, textWidth);

    return dimensionDescriptor;
  }

  protected setMinMaxValues(dimensionDescriptor: DimensionDescriptor, startPoint: Vector3, endPoint: Vector3, textWidth: number): void {
    if (dimensionDescriptor.isOuterLabel) {
      dimensionDescriptor.maxX = startPoint.x + textWidth + DimensionElement.OUTSIDE_ARROW_LINE_LENGTH + this.DIMENSION_SPACING;
      dimensionDescriptor.minX = endPoint.x - DimensionElement.OUTSIDE_ARROW_LINE_LENGTH - this.DIMENSION_SPACING;
    } else {
      dimensionDescriptor.minX = startPoint.x - this.DIMENSION_SPACING;
      dimensionDescriptor.maxX = endPoint.x + this.DIMENSION_SPACING;
    }
  }

  protected findMaxZValue(): number {
    let result: number = Number.MIN_VALUE;
    const articles: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    for (const article of articles) {
      const boundingPoints: Vector3[] = this.findBoundingPointsInGlobal(article);
      for (const point of boundingPoints) {
        if (result < point.z) {
          result = point.z;
        }
      }
    }
    return result + this.OFFSET;
  }

  protected formatDimension(dimension: number): string {
    return this.formatDimensionNumber(dimension) + ' ' + this.localeResourceManager.getValueByKey(this.MILLIMETER_UNIT_LOCALE_KEY);
  }

  protected createTextValue(value: string, color: RGBColorValueVO = null): SimpleTextValueVO {
    const simpleTextValue: SimpleTextValueVO = new SimpleTextValueVO();
    simpleTextValue.text = value;
    if (color) {
      simpleTextValue.bgColor = color;
    }
    return simpleTextValue;
  }

  private formatDimensionNumber(dimension: number): string {
    return this.localeResourceManager.formatNumber(dimension, false, 1, true);
  }

  protected createVerticalLineDimensionLabel(text: string, labelPositionX: number): LabelElement {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    const labelLength: number = DimensionUtils.getTextWidth(textValue, LabelElement.DEFAULT_FONT_SIZE);
    const lineDimensionLabel: LabelElement = new LabelElement(
      textValue,
      LabelElement.DEFAULT_FONT_SIZE,
      new Vector3(90, 180, 90),
      labelLength
    );
    lineDimensionLabel.position.set(labelPositionX + 20, 0, 0);
    return lineDimensionLabel;
  }

  protected createVerticalArrow(pointTop: Vector3, pointBottom: Vector3): ILayerElement[] {
    const arrowLength: number = this.dimensionsArrowLength * -1;
    const pointTopRight: Vector3 = new Vector3(pointTop.x + arrowLength, pointTop.y, pointTop.z - this.dimensionsArrowRadius);
    const pointTopLeft: Vector3 = new Vector3(pointTop.x - arrowLength, pointTop.y, pointTop.z - this.dimensionsArrowRadius);
    const pointBottomLeft: Vector3 = new Vector3(
      pointBottom.x - arrowLength,
      pointBottom.y,
      pointBottom.z + this.dimensionsArrowRadius
    );
    const pointBottomRight: Vector3 = new Vector3(
      pointBottom.x + arrowLength,
      pointBottom.y,
      pointBottom.z + this.dimensionsArrowRadius
    );

    return [
      DimensionUtils.createLineSegment(pointTop, pointBottom, DimensionElement.LINE_COLOR),
      DimensionUtils.createLineSegment(pointTop, pointTopRight, DimensionElement.LINE_COLOR),
      DimensionUtils.createLineSegment(pointTop, pointTopLeft, DimensionElement.LINE_COLOR),
      FaceUtils.createSingleFaceMesh(pointTopRight, pointTopLeft, pointTop, this.blackMaterial),
      DimensionUtils.createLineSegment(pointBottom, pointBottomLeft, DimensionElement.LINE_COLOR),
      DimensionUtils.createLineSegment(pointBottom, pointBottomRight, DimensionElement.LINE_COLOR),
      FaceUtils.createSingleFaceMesh(pointBottomLeft, pointBottomRight, pointBottom, this.blackMaterial),
      ...this.createLineAboveArrows(pointTop, pointBottom),
    ] as ILayerElement[];

  }

  private createLineAboveArrows(arrowPointTop: Vector3, arrowPointBottom: Vector3): Object3D[] {
    const pointTopRight: Vector3 = new Vector3(arrowPointTop.x + this.lineAboveArrowLength, arrowPointTop.y, arrowPointTop.z);
    const pointTopLeft: Vector3 = new Vector3(arrowPointTop.x - this.lineAboveArrowLength, arrowPointTop.y, arrowPointTop.z);

    const pointBottomRight: Vector3 = new Vector3(arrowPointBottom.x + this.lineAboveArrowLength, arrowPointBottom.y, arrowPointBottom.z);
    const pointBottomLeft: Vector3 = new Vector3(arrowPointBottom.x - this.lineAboveArrowLength, arrowPointBottom.y, arrowPointBottom.z);
    return [
      DimensionUtils.createLineSegment(pointTopLeft, pointTopRight, DimensionElement.LINE_COLOR),
      DimensionUtils.createLineSegment(pointBottomLeft, pointBottomRight, DimensionElement.LINE_COLOR)
    ];
  }

  protected collectStartEndDimensionDescriptors(
    startPoint: Vector3,
    endPoint: Vector3,
    articles: ArticleContainer[],
    usedMinLevel: number
  ): DimensionDescriptor[] {
    if (ArrayUtils.isEmpty(articles)) {
      return [];
    }

    let startEndDescriptors: DimensionDescriptor[] = [];

    // create descriptors for outer measurements of first/last drilling hole
    const outerDescriptors: DimensionDescriptor[] = this.createOuterStartEndDescriptors(startPoint, endPoint, articles, 0);
    // determine already used measurement-level to place inner start/end measurements below it
    usedMinLevel = this.updateMinLevelValueByDescriptors(outerDescriptors, usedMinLevel);
    startEndDescriptors = startEndDescriptors.concat(outerDescriptors);

    // create descriptors for inner measurements of first/last drilling hole
    const innerDescriptors: DimensionDescriptor[] = this.createInnerStartEndDescriptors(
      startPoint,
      endPoint,
      articles,
      NumberUtils.isNotNumber(usedMinLevel) ? 0 : usedMinLevel - 1
    );
    startEndDescriptors = startEndDescriptors.concat(innerDescriptors);

    return startEndDescriptors;
  }

  protected calculateOffset(article: ArticleContainer): number {
    const minInGlobal: Vector3 = this.calculateMinGlobalArticlePoint(article);
    const maxInGlobal: Vector3 = this.calculateMaxGlobalArticlePoint(article);

    return (minInGlobal.x + maxInGlobal.x) / 2;
  }

  protected calculateWidth(article: ArticleContainer): number {
    const minInGlobal: Vector3 = this.calculateMinGlobalArticlePoint(article);
    const maxInGlobal: Vector3 = this.calculateMaxGlobalArticlePoint(article);
    const point: Vector3 = new Vector3(minInGlobal.x, maxInGlobal.y, maxInGlobal.z);
    return point.distanceTo(maxInGlobal);
  }

  protected multiplyVector(vector: Vector3, scalar: number): Vector3 {
    return vector.clone().multiplyScalar(scalar);
  }

  protected updateMinLevelValueByDescriptors(descriptors: DimensionDescriptor[], minLevel: number = NaN): number {
    let calculatedMinLevel: number = minLevel;
    for (const descriptor of descriptors) {
      calculatedMinLevel = NumberUtils.isNotNumber(calculatedMinLevel) ? descriptor.sizeLevel : Math.min(calculatedMinLevel, descriptor.sizeLevel);
    }
    return calculatedMinLevel;
  }

  protected collectLabels(drillingHoleContainers: ArticleContainer[]): Map<ArticleContainer, string[]> {
    const labelsMap: Map<ArticleContainer, string[]> = new Map<ArticleContainer, string[]>();
    for (const drillingHoleContainer of drillingHoleContainers) {
      const articleContainers: ArticleContainer[] = this.articleContainerUtils.getConnectedArticlesRecursively(drillingHoleContainer);
      const articleNumbers: string[] = articleContainers.map((container: ArticleContainer) => container.article.articleNo);
      if (ArrayUtils.isEmpty(articleNumbers)) {
        continue;
      }
      const labels: string[] = this.getLabelsByArticleNumbers(articleNumbers);
      labelsMap.set(drillingHoleContainer, labels);
    }
    return labelsMap;
  }

  private createStartEndDimensionDescriptor(startPoint: Vector3, endPoint: Vector3, level: number): DimensionDescriptor {
    const descriptor: DimensionDescriptor = this.createDimensionDescriptor(
      startPoint,
      endPoint,
      false,
      this.createTextValue(this.formatDimension(startPoint.distanceTo(endPoint) / 10))
    );
    descriptor.sizeLevel = level;
    return descriptor;
  }

  /**
   * Creates measurements for the first and last drilling-hole (which are placed on rail):
   * - from start of rail to beginning of first drilling-hole on rail
   * - from end of last drilling-hole on rail to end of rail
   */
  private createInnerStartEndDescriptors(startPoint: Vector3, endPoint: Vector3, articles: ArticleContainer[], level: number): DimensionDescriptor[] {
    const startEndDescriptors: DimensionDescriptor[] = [];
    const normal: Vector3 = this.getDirectionNormal(endPoint, startPoint);

    const firstArticle: DrillingHoleSizeData | undefined = this.findFirstDrillingHoleOnRail(articles, startPoint, endPoint, false);
    if (firstArticle && this.isInnerStartEndMeasurementIncluded(firstArticle, startPoint, endPoint)) {
      const firstArticleStartPosition: Vector3 = startPoint.clone().add(this.multiplyVector(normal, firstArticle.start));
      const startDescriptor: DimensionDescriptor = this.createStartEndDimensionDescriptor(startPoint, firstArticleStartPosition, level);
      startEndDescriptors.push(startDescriptor);
    }
    const lastArticle: DrillingHoleSizeData | undefined = this.findFirstDrillingHoleOnRail(articles, startPoint, endPoint, true);
    if (lastArticle && this.isInnerStartEndMeasurementIncluded(lastArticle, startPoint, endPoint)) {
      const lastArticleEndPosition: Vector3 = startPoint.clone().add(this.multiplyVector(normal, lastArticle.end));
      const endDescriptor: DimensionDescriptor = this.createStartEndDimensionDescriptor(lastArticleEndPosition, endPoint, level);
      startEndDescriptors.push(endDescriptor);
    }
    return startEndDescriptors;
  }

  private createOuterStartEndDescriptors(startPoint: Vector3, endPoint: Vector3, articles: ArticleContainer[], level: number): DimensionDescriptor[] {
    const startEndDescriptors: DimensionDescriptor[] = [];
    let descriptor: DimensionDescriptor;
    const mountingRailVector: Vector3 = this.getDirectionNormal(startPoint, endPoint);

    const firstArticle: ArticleContainer = articles[0];
    let articleOffset: number = this.calculateOffset(firstArticle);
    let articleWidth: number = this.calculateWidth(firstArticle);

    if (articleOffset <= startPoint.x) {
      const articleEndPosition = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(articleOffset) + articleWidth / 2));
      descriptor = this.createStartEndDimensionDescriptor(startPoint, articleEndPosition, level);
      startEndDescriptors.push(descriptor);
    }

    const lastArticle: ArticleContainer = articles[articles.length - 1];
    articleOffset = this.calculateOffset(lastArticle);
    articleWidth = this.calculateWidth(lastArticle);

    if (lastArticle !== firstArticle) {
      const articleStartPosition = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(articleOffset) - articleWidth / 2));
      descriptor = this.createStartEndDimensionDescriptor(articleStartPosition, endPoint, level);
      startEndDescriptors.push(descriptor);
    }
    return startEndDescriptors;
  }

  private getDirectionNormal(startPoint: Vector3, endPoint: Vector3): Vector3 {
    const normal: Vector3 = endPoint.clone().sub(startPoint);
    normal.normalize();
    return normal;
  }

  private isInnerStartEndMeasurementIncluded(drillingHole: DrillingHoleSizeData, startPoint: Vector3, endPoint: Vector3): boolean {
    if (!drillingHole) {
      return false;
    }
    // measurement has to be shown only for drilling-hole completely placed on rail (without overhangs)
    return MathUtils.compare(startPoint.x, drillingHole.start) >= 0 && MathUtils.compare(endPoint.x, drillingHole.end) <= 0;
  }

  /**
   * @return first found drilling-hole that at least partially touches the rail,
   * so first/last drilling-holes which are placed outside the rail are ignored
   */
  private findFirstDrillingHoleOnRail(
    drillingHoleContainers: ArticleContainer[],
    startPoint: Vector3,
    endPoint: Vector3,
    reverse: boolean
  ): DrillingHoleSizeData | undefined {
    const containersToCheck: ArticleContainer[] = drillingHoleContainers.concat();
    if (reverse) {
      containersToCheck.reverse();
    }
    for (const drillingHoleContainer of containersToCheck) {
      const drillingHoleSize: DrillingHoleSizeData = this.createDrillingHoleSize(drillingHoleContainer);
      if (MathUtils.compare(startPoint.x, drillingHoleSize.end) > 0 && MathUtils.compare(endPoint.x, drillingHoleSize.start) < 0) {
        // at least part of the drilling hole placed on rail
        return drillingHoleSize;
      }
    }
    return undefined;
  }

  private createDrillingHoleSize(drillingHoleContainer: ArticleContainer): DrillingHoleSizeData {
    const articleOffset: number = this.calculateOffset(drillingHoleContainer);
    const articleWidth: number = this.calculateWidth(drillingHoleContainer);
    return new DrillingHoleSizeData(drillingHoleContainer, articleOffset, articleWidth);
  }

  private getLabelsByArticleNumbers(articleNumbers: string[]): string[] {
    articleNumbers.sort((a: string, b: string) => StringUtils.stringCompare(a, b));
    const labels: string[] = [];
    let count: number = 1;

    articleNumbers.forEach((articleNumber: string, index: number) => {
      if (articleNumber === articleNumbers[index + 1]) {
        count++;
      } else {
        const label: string = this.getArticleNumberLabel(articleNumber, count);
        this.updateLabelsLength(label);
        labels.push(label);
        count = 1;
      }
    });

    return labels;
  }

  private getArticleNumberLabel(articleNo: string, count: number): string {
    return count > 1 ? count.toString() + BaseLayerElementCreator.X_DELIMITER + articleNo : articleNo;
  }

  private updateLabelsLength(label: string): void {
    const labelLength: number = DimensionUtils.getTextLength(label);
    this.longestLabelLength = Math.max(this.longestLabelLength, labelLength);
  }
}
